// src/pages/AttendanceDashboard.jsx
import React, { Fragment, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import API from "../../services/API";

// ==================== API HELPERS ====================
const getTokenHeaders = () => ({
    token: localStorage.getItem("token"),
});

const fetchAttendance = (params = {}) =>
    API.get("/goodDeeds/getAttendance", {
        headers: getTokenHeaders(),
        params: { is_portal: true, ...params },
    });

const getTodayLocal = () => {
    const now = new Date();
    const offset = now.getTimezoneOffset();

    return new Date(now.getTime() - offset * 60 * 1000)
        .toISOString()
        .slice(0, 10);
};

// ==================== UTILS ====================
const safeArray = (value) => (Array.isArray(value) ? value : []);

const formatNumber = (value) =>
    Number(value || 0).toLocaleString("en-MY");

const formatTime = (value) => {
    if (!value) return "-";

    const stringValue = String(value);

    if (/^\d{2}:\d{2}(:\d{2})?$/.test(stringValue)) {
        return stringValue.slice(0, 5);
    }

    const parsed = new Date(stringValue);

    if (Number.isNaN(parsed.getTime())) {
        return stringValue;
    }

    return parsed.toLocaleTimeString("en-MY", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    });
};

const getInitial = (name) => {
    if (!name) return "?";
    return String(name).trim().charAt(0).toUpperCase();
};

const normalizeText = (value) =>
    String(value || "").trim().toLowerCase();

// ==================== COMPONENTS ====================
const PageShell = ({ children }) => (
    <div
        className="container-fluid"
        style={{
            minHeight: "100vh",
            background:
                "radial-gradient(circle at top left, rgba(37,99,235,0.08), transparent 30%), linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)",
            padding: "1.25rem",
        }}
    >
        {children}
    </div>
);

const SectionCard = ({ title, subtitle, action, children, style }) => (
    <div
        className="card border-0 mb-4"
        style={{
            borderRadius: 24,
            overflow: "hidden",
            boxShadow: "0 16px 45px rgba(15, 23, 42, 0.08)",
            border: "1px solid rgba(226, 232, 240, 0.9)",
            ...style,
        }}
    >
        {(title || subtitle || action) && (
            <div
                className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-3"
                style={{
                    padding: "1.15rem 1.25rem",
                    borderBottom: "1px solid #e2e8f0",
                    background: "rgba(255,255,255,0.85)",
                }}
            >
                <div>
                    {title && (
                        <h5
                            className="mb-1"
                            style={{
                                fontWeight: 900,
                                color: "#0f172a",
                                letterSpacing: "-0.02em",
                            }}
                        >
                            {title}
                        </h5>
                    )}

                    {subtitle && (
                        <div
                            style={{
                                color: "#64748b",
                                fontSize: "0.88rem",
                                fontWeight: 600,
                            }}
                        >
                            {subtitle}
                        </div>
                    )}
                </div>

                {action}
            </div>
        )}

        <div style={{ padding: "1.25rem", background: "#fff" }}>
            {children}
        </div>
    </div>
);

const EmptyState = () => (
    <div
        className="text-center"
        style={{
            padding: "3rem 1rem",
            borderRadius: 20,
            border: "1px dashed #cbd5e1",
            background: "#f8fafc",
        }}
    >
        <div style={{ fontSize: "2rem", marginBottom: 8 }}>📭</div>
        <h6 style={{ fontWeight: 900, color: "#334155" }}>
            No attendance records
        </h6>
        <div style={{ color: "#64748b", fontSize: "0.9rem" }}>
            Try another attendance date.
        </div>
    </div>
);

const StudentAvatar = ({ name }) => (
    <div
        style={{
            width: 42,
            height: 42,
            borderRadius: 14,
            background: "linear-gradient(135deg, #2563eb, #111827)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontWeight: 900,
            flexShrink: 0,
        }}
    >
        {getInitial(name)}
    </div>
);

// ==================== MAIN COMPONENT ====================
const AttendanceDashboard = () => {
    const [selectedDate, setSelectedDate] = useState(getTodayLocal());
    const [searchTerm, setSearchTerm] = useState("");
    const [attendance, setAttendance] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let isMounted = true;

        const loadAttendance = async () => {
            setLoading(true);

            try {
                const response = await fetchAttendance({
                    date: selectedDate,
                });

                if (!isMounted) return;

                if (response?.data?.response_code === 1000) {
                    setAttendance(safeArray(response.data.data));
                    setTotal(
                        Number(
                            response.data.total ??
                            response.data.data?.length ??
                            0
                        )
                    );
                } else {
                    setAttendance([]);
                    setTotal(0);
                    toast.error(
                        response?.data?.message ||
                        "Failed to load attendance"
                    );
                }
            } catch (error) {
                if (!isMounted) return;

                console.error("Failed to fetch attendance:", error);
                setAttendance([]);
                setTotal(0);

                toast.error(
                    error?.response?.data?.message ||
                    error?.message ||
                    "Failed to load attendance"
                );
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        loadAttendance();

        return () => {
            isMounted = false;
        };
    }, [selectedDate]);

    const filteredAttendance = useMemo(() => {
        const query = normalizeText(searchTerm);

        if (!query) return attendance;

        return attendance.filter((student) => {
            return [
                student?.student_name,
                student?.first_name,
                student?.student_id,
                student?.class_name,
                student?.class_id,
            ].some((value) =>
                normalizeText(value).includes(query)
            );
        });
    }, [attendance, searchTerm]);

    return (
        <Fragment>
            <PageShell>
                <div
                    className="d-flex flex-column flex-xl-row justify-content-between align-items-start align-items-xl-center gap-3 mb-4"
                    style={{
                        background:
                            "linear-gradient(135deg, #1e3a8a 0%, #2563eb 48%, #0ea5e9 100%)",
                        borderRadius: 28,
                        padding: "1.5rem",
                        color: "#fff",
                        boxShadow: "0 24px 60px rgba(37, 99, 235, 0.25)",
                    }}
                >
                    <div>
                        <button
                            type="button"
                            className="btn btn-sm mb-3"
                            onClick={() =>
                                window.location.assign("/amalanBaikDashboard")
                            }
                            style={{
                                borderRadius: 999,
                                background: "rgba(255,255,255,0.14)",
                                border: "1px solid rgba(255,255,255,0.25)",
                                color: "#fff",
                                fontWeight: 800,
                            }}
                        >
                            ← Back to Amalan Baik
                        </button>

                        <h2
                            className="mb-1"
                            style={{
                                fontWeight: 950,
                                letterSpacing: "-0.04em",
                                fontSize: "2rem",
                            }}
                        >
                            Attendance Dashboard
                        </h2>

                        <div
                            style={{
                                color: "rgba(255,255,255,0.82)",
                                fontWeight: 600,
                            }}
                        >
                            View students who checked in for the selected date.
                        </div>
                    </div>

                    <div
                        style={{
                            background: "rgba(255,255,255,0.12)",
                            border: "1px solid rgba(255,255,255,0.25)",
                            borderRadius: 18,
                            padding: "0.85rem",
                        }}
                    >
                        <div
                            style={{
                                fontSize: "0.72rem",
                                fontWeight: 900,
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                                color: "rgba(255,255,255,0.75)",
                                marginBottom: 5,
                            }}
                        >
                            Attendance Date
                        </div>

                        <input
                            type="date"
                            className="form-control"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            style={{
                                borderRadius: 14,
                                border: "none",
                                minWidth: 180,
                                fontWeight: 800,
                                color: "#0f172a",
                            }}
                        />
                    </div>
                </div>

                <div className="row g-3 mb-4">
                    <div className="col-12 col-md-6">
                        <div
                            style={{
                                borderRadius: 22,
                                padding: "1rem",
                                background:
                                    "linear-gradient(135deg, #eff6ff 0%, #fff 70%)",
                                border: "1px solid #bfdbfe",
                                boxShadow:
                                    "0 14px 35px rgba(15,23,42,0.06)",
                            }}
                        >
                            <div
                                style={{
                                    color: "#64748b",
                                    fontSize: "0.72rem",
                                    fontWeight: 900,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.08em",
                                }}
                            >
                                Total Checked In
                            </div>

                            <div
                                style={{
                                    fontSize: "2rem",
                                    fontWeight: 950,
                                    color: "#0f172a",
                                    marginTop: 6,
                                }}
                            >
                                {formatNumber(total)}
                            </div>
                        </div>
                    </div>
                </div>

                <SectionCard
                    title="Students Checked In"
                    subtitle={`Check-ins for ${selectedDate}`}
                    action={
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search student or class"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                borderRadius: 14,
                                minWidth: 240,
                                fontWeight: 600,
                            }}
                        />
                    }
                >
                    {loading ? (
                        <div
                            className="d-flex justify-content-center py-5"
                        >
                            <div
                                className="spinner-border text-primary"
                                role="status"
                            />
                        </div>
                    ) : filteredAttendance.length === 0 ? (
                        <EmptyState />
                    ) : (
                        <div className="table-responsive">
                            <table className="table align-middle mb-0">
                                <thead>
                                    <tr>
                                        <th>Student</th>
                                        <th>Class</th>
                                        <th>Check-in</th>
                                        <th>Check-out</th>
                                        <th>Image</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {filteredAttendance.map(
                                        (student, index) => {
                                            const studentName =
                                                student?.student_name ||
                                                student?.first_name ||
                                                "Unknown Student";

                                            const image =
                                                student?.check_in_image_url;

                                            return (
                                                <tr
                                                    key={
                                                        student?.attendance_id ||
                                                        `${student?.student_id}-${index}`
                                                    }
                                                >
                                                    <td>
                                                        <div className="d-flex align-items-center gap-3">
                                                            <StudentAvatar
                                                                name={
                                                                    studentName
                                                                }
                                                            />

                                                            <div>
                                                                <div
                                                                    style={{
                                                                        fontWeight: 900,
                                                                        color: "#0f172a",
                                                                    }}
                                                                >
                                                                    {
                                                                        studentName
                                                                    }
                                                                </div>

                                                                <small
                                                                    style={{
                                                                        color: "#64748b",
                                                                    }}
                                                                >
                                                                    {student?.student_id ||
                                                                        "-"}
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </td>

                                                    <td>
                                                        <div
                                                            style={{
                                                                fontWeight: 800,
                                                                color: "#334155",
                                                            }}
                                                        >
                                                            {student?.class_name ||
                                                                student?.class_id ||
                                                                "-"}
                                                        </div>
                                                    </td>

                                                    <td
                                                        style={{
                                                            fontWeight: 800,
                                                        }}
                                                    >
                                                        {formatTime(
                                                            student?.check_in_time
                                                        )}
                                                    </td>

                                                    <td>
                                                        {formatTime(
                                                            student?.check_out_time
                                                        )}
                                                    </td>

                                                    <td>
                                                        {image &&
                                                            image !== "N/A" ? (
                                                            <img
                                                                src={image}
                                                                alt={`${studentName} check-in`}
                                                                style={{
                                                                    width: 140,
                                                                    height: 100,
                                                                    objectFit: "cover",
                                                                    borderRadius: 12,
                                                                    border: "1px solid #e2e8f0",
                                                                    cursor: "pointer",
                                                                }}
                                                            />
                                                        ) : (
                                                            "-"
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        }
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </SectionCard>
            </PageShell>
        </Fragment>
    );
};

export default AttendanceDashboard;